import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import { PositionBookLookupService } from "./PositionBookLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { IPositionBookPromiseLookupService } from "../IPositionBookPromiseLookupService";
import { BaseLookupService } from "../../impl/BaseLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { CcyPairLookupService } from "./CcyPairLookupService";
import { Subscription } from "rxjs";
import { PositionBookFetchResponseImpl } from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class PositionBookPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.PositionBookImpl>
  implements IPositionBookPromiseLookupService
{
  private defaultPositionBookId: string;
  private defaultPositionBook: any;
  private userUpdateSub: Subscription;
  private userBranchCode: string;
  constructor(
    protected base: PositionBookLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    private activeUserService: ActiveUserProfilePromiseService,
    private ccyPairService: CcyPairLookupService,
  ) {
    super(base, factory, CubiqTS.PositionBookImpl, cubiqWorker, "PositionBookImpl");

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        activeUserService.getActiveUserData().then((user) => {
          this.base.defaultPositionBookId = user.defaultPositionBookId;
          this.defaultPositionBookId = user.defaultPositionBookId;
          this.userBranchCode = user.branchCd;
          this.lookupPositionBook(user.branchCd).then((values) => {
            this.prime(values);
            this._primed.resolve(true);
            this.defaultPositionBook = this.adaptPositionBook(
              this.base.getUserDefaultPositionBook(),
            );
          });
        });
        this.userUpdateSub = activeUserService.getUserUpdateSubject().subscribe((user) => {
          this.base.defaultPositionBookId = user.defaultPositionBookId;
          this.defaultPositionBookId = user.defaultPositionBookId;
          this.defaultPositionBook = this.adaptPositionBook(this.base.getUserDefaultPositionBook());
        });
      },
      (reason) => {
        console.debug("PositionBookPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod(
      "getPositionBook",
      [PRIMITIVE],
      this.getPositionBook.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getAdaptedPositionBooks",
      [],
      this.getAdaptedPositionBooks.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getAdaptedPositionBook",
      [PRIMITIVE],
      this.getAdaptedPositionBook.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getAdaptedUserDefaultPositionBook",
      [],
      this.getAdaptedUserDefaultPositionBook.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getUserDefaultPositionBook",
      [],
      this.getUserDefaultPositionBook.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getPositionBooksForPairAndTradingGroup",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.getPositionBooksForPairAndTradingGroup.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getPositionBooksForPairAndUserBranch",
      [PRIMITIVE, PRIMITIVE],
      this.getPositionBooksForPairAndUserBranch.bind(this),
      PRIMITIVE,
    );
  }

  primePosBooks(): void {}

  getPositionBook(code: string): Promise<CubiqTS.PositionBookImpl> {
    return new Promise<CubiqTS.PositionBookImpl>((res, rej) => {
      res(this.base.getPositionBook(code));
    });
  }

  getAdaptedPositionBook(code: string): Promise<any> {
    return new Promise<any>((res, rej) => {
      let pb = this.base.getPositionBook(code);
      res(this.adaptPositionBook(pb));
    });
  }
  getAdaptedUserDefaultPositionBook(): Promise<any> {
    return new Promise<any>((res, rej) => {
      let pb = this.base.getUserDefaultPositionBook();
      res(this.adaptPositionBook(pb));
    });
  }

  getUserDefaultPositionBook(): Promise<CubiqTS.PositionBookImpl> {
    return new Promise<CubiqTS.PositionBookImpl>((res, rej) => {
      res(this.base.getUserDefaultPositionBook());
    });
  }

  getAdaptedPositionBooks(query: string = ""): Promise<Array<any>> {
    return new Promise<Array<any>>((res, rej) => {
      let results: Array<any> = this.base.getAll().map((result: CubiqTS.PositionBookImpl) => {
        return this.adaptPositionBook(result);
      });
      res(results);
    });
  }

  getPositionBooks(query: string = ""): Promise<Array<CubiqTS.PositionBookImpl>> {
    return new Promise<Array<CubiqTS.PositionBookImpl>>((res, rej) => {
      res(this.base.getAll());
    });
  }

  getBase(): PositionBookLookupService {
    return this.base;
  }

  private lookupPositionBook(
    branchCode: string,
    filterMatch: string = null,
    maxResults: number = null,
  ): Promise<Array<any>> {
    let request: CubiqTS.PositionBookFilterMatchRequestImpl =
      new CubiqTS.PositionBookFilterMatchRequestImpl();
    request.branchCode = branchCode;

    if (typeof maxResults === "number") request.maxResults = maxResults;
    if (typeof filterMatch === "string") request.nameFilter = filterMatch;

    return new Promise<Array<any>>((resolve, reject) => {
      this.cubiqWorker.postServiceRequest(request).then((value: any) => {
        if (value.encodableType == "com.bcdfx.service.StandardReportImpl") {
          let report: CubiqTS.StandardReportImpl = value as CubiqTS.StandardReportImpl;
          resolve(report.results);
        } else {
          value.error = true;
          resolve(value);
        }
      });
    });
  }

  getPositionBooksForPairAndTradingGroup(
    base: string,
    nonBase: string,
    traderGroupId: number,
    vehicleCcy?: string,
  ): Promise<any> {
    let request: CubiqTS.PositionBookRequestImpl = new CubiqTS.PositionBookRequestImpl();
    let pair = this.ccyPairService.getCcyPair(base, nonBase);
    if (!pair) {
      pair = this.ccyPairService.getCcyPair(nonBase, base);
    }
    if (pair) {
      request.baseCurrencyCd = pair.base;
      request.nonBaseCurrencyCd = pair.nonBase;
    }
    request.traderGroupId = new CubiqTS.Long(traderGroupId);
    request.crossCcy = vehicleCcy;
    return new Promise<any>((resolve, reject) => {
      this.cubiqWorker.postServiceRequest(request).then((value: any) => {
        if (value.encodableType == "com.bcdfx.model.PositionBookFetchResponseImpl") {
          let response: CubiqTS.PositionBookFetchResponseImpl =
            value as CubiqTS.PositionBookFetchResponseImpl;
          if (response) {
            let root = this.adaptBookResponse(response.positionBooks);

            if (response.componentPositionBooks && response.componentPositionBooks.length > 0) {
              let leg1 = this.adaptBookResponse(response.componentPositionBooks[0]);
              let leg2 = this.adaptBookResponse(response.componentPositionBooks[1]);
              resolve({ root: root, leg1: leg1, leg2: leg2 });
            } else {
              resolve({ root: root });
            }
          }
        } else if (value.encodableType == "com.bcdfx.service.StandardReportImpl") {
          // this will only get hit with older impl, once we migrate to 4.2 this can be deleted
          let report: CubiqTS.StandardReportImpl = value as CubiqTS.StandardReportImpl;
          if (report.results) {
            let results: Array<any> = report.results.map((result: CubiqTS.PositionBookImpl) => {
              return this.adaptPositionBook(result);
            });
            this.includeDefaultPositionBook(results);
            resolve({ root: { books: results } });
          } else {
            resolve(null);
          }
        } else {
          value.error = true;
          resolve(value);
        }
      });
    });
  }

  private adaptBookResponse(posBook: CubiqTS.PositionBookPairInfoImpl): any {
    let book: any = this.adaptPositionBook(posBook.defaultBook);
    let books: Array<any> = posBook.positionBooks.map((result: CubiqTS.PositionBookImpl) => {
      return this.adaptPositionBook(result);
    });
    this.includeDefaultPositionBook(books);
    return { book: book, books: books };
  }

  private adaptPositionBook(book: CubiqTS.PositionBook): any {
    if (book)
      return {
        value: book.positionBookId.toString(),
        label: book.positionBookName,
        branch: book.positionBookBranch,
        isDefault: this.defaultPositionBookId == book.positionBookId.toString() ? true : false,
      };
    return null;
  }

  getPositionBooksForPairAndUserBranch(base: string, nonBase: string): Promise<Array<any>> {
    return this.getPositionBooksForPairAndBranch(base, nonBase, this.userBranchCode);
  }

  getPositionBooksForPairAndBranch(
    base: string,
    nonBase: string,
    branchCd: string,
  ): Promise<Array<any>> {
    let request: CubiqTS.PositionBookRequestImpl = new CubiqTS.PositionBookRequestImpl();
    let pair = this.ccyPairService.getCcyPair(base, nonBase);

    if (!pair) {
      pair = this.ccyPairService.getCcyPair(nonBase, base);
    }

    if (pair) {
      request.baseCurrencyCd = pair.base;
      request.nonBaseCurrencyCd = pair.nonBase;
    }

    request.branchCd = branchCd;

    return new Promise<Array<any>>((resolve, reject) => {
      this.cubiqWorker.postServiceRequest(request).then((value: any) => {
        if (value.encodableType == "com.bcdfx.service.StandardReportImpl") {
          let report: CubiqTS.StandardReportImpl = value as CubiqTS.StandardReportImpl;
          if (report.results) {
            let results: Array<any> = report.results.map((result: CubiqTS.PositionBookImpl) => {
              return this.adaptPositionBook(result);
            });
            this.includeDefaultPositionBook(results);
            resolve(results);
          } else {
            resolve([]);
          }
        } else {
          value.error = true;
          resolve(value);
        }
      });
    });
  }

  private includeDefaultPositionBook(results: Array<any>): void {
    if (this.defaultPositionBook) {
      let included: boolean = false;
      for (let book of results) {
        if (book.value == this.defaultPositionBookId) {
          included = true;
          break;
        }
      }
      if (!included) {
        results.unshift(this.defaultPositionBook);
      }
    }
  }
}
